import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { InfoIcon } from "../../../../assets/icons";
import { PERIOD_OPTIONS } from "../../history/filter-page/history.filters";
import styles from "./period-filter.module.scss";

export interface PeriodValue {
	period: string | null;
	startDate: Date | null;
	endDate: Date | null;
}

interface PeriodFilterProps {
	value: PeriodValue;
	onChange: (next: PeriodValue) => void;
	title?: string;
	tooltipText?: string;
	accentColor?: string;
	className?: string;
}

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

// Preset periods resolve to a concrete start/end range; "Custom" (null) is handled by the caller path below.
const computeDatesForPeriod = (period: string | null): { startDate: Date | null; endDate: Date | null } => {
	if (!period) return { startDate: null, endDate: null };
	const now = new Date();
	const dayOfWeek = now.getDay();
	if (period === "currentWeek") {
		const start = new Date(now);
		start.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
		return { startDate: startOfDay(start), endDate: endOfDay(new Date(now)) };
	}
	if (period === "lastWeek") {
		const start = new Date(now);
		start.setDate(now.getDate() - ((dayOfWeek + 6) % 7) - 7);
		const end = new Date(start);
		end.setDate(start.getDate() + 6);
		return { startDate: startOfDay(start), endDate: endOfDay(end) };
	}
	if (period === "currentMonth") {
		const start = new Date(now.getFullYear(), now.getMonth(), 1);
		return { startDate: startOfDay(start), endDate: endOfDay(new Date(now)) };
	}
	if (period === "lastMonth") {
		const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
		const end = new Date(now.getFullYear(), now.getMonth(), 0);
		return { startDate: startOfDay(start), endDate: endOfDay(end) };
	}
	if (period === "currentYear") {
		const start = new Date(now.getFullYear(), 0, 1);
		return { startDate: startOfDay(start), endDate: endOfDay(new Date(now)) };
	}
	if (period === "lastYear") {
		const start = new Date(now.getFullYear() - 1, 0, 1);
		const end = new Date(now.getFullYear() - 1, 11, 31);
		return { startDate: startOfDay(start), endDate: endOfDay(end) };
	}
	return { startDate: null, endDate: null };
};

const toDateInputValue = (date: Date | null): string => (date ? date.toISOString().split("T")[0] : "");

const formatDisplayDate = (date: Date | null): string =>
	date ? date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

const PeriodFilter = ({ value, onChange, title, tooltipText, accentColor, className }: PeriodFilterProps) => {
	const { t } = useTranslation();
	const handlePeriodSelect = (period: string | null) => {
		// Custom (null): default both dates to today; presets compute their own range.
		if (period === null) {
			const today = new Date();
			onChange({ period: null, startDate: startOfDay(today), endDate: endOfDay(today) });
			return;
		}
		onChange({ period, ...computeDatesForPeriod(period) });
	};

	const handleDateChange = (field: "startDate" | "endDate", raw: string) => {
		if (!raw) {
			onChange({ ...value, [field]: null });
			return;
		}
		const [year, month, day] = raw.split("-").map(Number);
		onChange({ ...value, [field]: new Date(year, month - 1, day) });
	};

	const rootStyle = accentColor ? ({ "--period-chip-active-bg": accentColor } as CSSProperties) : undefined;

	return (
		<section className={`${styles.section}${className ? ` ${className}` : ""}`} style={rootStyle}>
			<div className={styles.sectionHeader}>
				<span className={styles.sectionTitle}>{title ?? t("history.period")}</span>
				{tooltipText && (
					<span className={styles.infoTooltipWrapper}>
						<InfoIcon className={styles.infoIcon} />
						<span className={styles.tooltip}>{tooltipText}</span>
					</span>
				)}
			</div>
			<div className={styles.chipsPeriod}>
				{PERIOD_OPTIONS.map(opt => (
					<button
						type="button"
						key={String(opt.value)}
						className={`${styles.chip} ${value.period === opt.value ? styles.chipActive : ""}`}
						onClick={() => handlePeriodSelect(opt.value)}
					>
						{opt.label}
					</button>
				))}
			</div>
			<div className={styles.dateRow}>
				<div className={styles.dateField}>
					<span className={styles.dateLabel}>{t("common.labels.startDate")}</span>
					<span className={styles.dateDisplayValue}>{formatDisplayDate(value.startDate)}</span>
					<input
						type="date"
						className={styles.dateInputHidden}
						value={toDateInputValue(value.startDate)}
						onChange={e => handleDateChange("startDate", e.target.value)}
					/>
				</div>
				<div className={styles.dateField}>
					<span className={styles.dateLabel}>{t("common.labels.endDate")}</span>
					<span className={styles.dateDisplayValue}>{formatDisplayDate(value.endDate)}</span>
					<input
						type="date"
						className={styles.dateInputHidden}
						value={toDateInputValue(value.endDate)}
						onChange={e => handleDateChange("endDate", e.target.value)}
					/>
				</div>
			</div>
		</section>
	);
};

export default PeriodFilter;
